<script setup lang="ts">
/**
 * Avertissement de session révoquée, avec compte à rebours avant déconnexion.
 *
 * Se présente d'abord en modale non refermable — la session est perdue, la
 * masquer ne changerait rien — puis en bandeau si l'éditeur choisit de mettre
 * son travail à l'abri. Les deux formes affichent le même décompte, pour qu'à
 * aucun moment on ne se demande combien de temps il reste.
 *
 * La détection est dans `useAuthHeartbeat` ; ce composant ne fait que
 * l'afficher.
 */
const { revoked, countdown, deferred, forceLogout } = useAuthHeartbeat()

const authStore = useAuthStore()

/** Réduit en bandeau : l'utilisateur a demandé à finir ce qu'il faisait. */
const minimized = ref(false)

// Deux fonctions plutôt qu'une affectation dans le gabarit : `@click` attend
// un gestionnaire qui ne renvoie rien, et `minimized = true` renvoie la valeur
// affectée.
function minimize() {
  minimized.value = true
}

function expand() {
  minimized.value = false
}

/**
 * L'avertissement ne s'affiche que sur une session encore ouverte de notre
 * point de vue : pendant une déconnexion volontaire ou une reconnexion, il
 * ferait doublon avec la redirection en cours.
 */
const visible = computed(() =>
  revoked.value
  && authStore.authenticated
  && !authStore.loggingOut
  && !deferred.value,
)

/** Part de temps restante, pour la barre de progression. */
const progress = computed(() => `${(countdown.value / 60) * 100}%`)
</script>

<template>
  <div>
    <UModal :open="visible && !minimized" :dismissible="false">
      <template #content>
        <div class="p-6 space-y-5 text-center">
          <div class="mx-auto w-14 h-14 rounded-full bg-[var(--ui-error)]/10 flex items-center justify-center">
            <UIcon name="i-lucide-shield-x" class="text-3xl text-[var(--ui-error)]" />
          </div>

          <div>
            <h3 class="text-lg font-semibold text-[var(--ui-text-highlighted)]">
              Session révoquée
            </h3>
            <p class="mt-2 text-sm text-[var(--ui-text-muted)]">
              Votre compte a été désactivé, ou la session n'est plus reconnue
              par le SSO. Enregistrez votre travail en cours avant la
              déconnexion.
            </p>
          </div>

          <div class="flex items-center justify-center gap-2 text-sm text-[var(--ui-text-dimmed)]">
            <UIcon name="i-lucide-timer" />
            <span>Déconnexion automatique dans <strong class="text-[var(--ui-text-highlighted)]">{{ countdown }} s</strong></span>
          </div>

          <div class="w-full h-1.5 rounded-full bg-[var(--ui-bg-elevated)] overflow-hidden">
            <div
              class="h-full rounded-full bg-[var(--ui-error)] transition-all duration-1000 ease-linear"
              :style="{ width: progress }"
            />
          </div>

          <div class="flex gap-2">
            <UButton
              variant="outline"
              color="neutral"
              class="flex-1 justify-center"
              icon="i-lucide-save"
              @click="minimize"
            >
              Enregistrer mon travail
            </UButton>
            <UButton
              color="error"
              class="flex-1 justify-center"
              icon="i-lucide-log-out"
              @click="forceLogout"
            >
              Me déconnecter
            </UButton>
          </div>
        </div>
      </template>
    </UModal>

    <Transition
      enter-active-class="transition-transform duration-300 ease-out"
      leave-active-class="transition-transform duration-200 ease-in"
      enter-from-class="translate-y-full"
      leave-to-class="translate-y-full"
    >
      <div
        v-if="visible && minimized"
        class="fixed bottom-0 inset-x-0 z-50 bg-[var(--ui-error)] text-white shadow-lg"
      >
        <div class="h-1 bg-white/20">
          <div
            class="h-full bg-white/60 transition-all duration-1000 ease-linear"
            :style="{ width: progress }"
          />
        </div>

        <div class="flex items-center justify-between px-4 py-2.5">
          <div class="flex items-center gap-2 text-sm">
            <UIcon name="i-lucide-shield-x" class="text-lg shrink-0" />
            <span>Session révoquée — déconnexion dans <strong>{{ countdown }} s</strong></span>
          </div>
          <div class="flex items-center gap-2 shrink-0">
            <UButton
              size="xs"
              variant="soft"
              color="neutral"
              class="text-white bg-white/15 hover:bg-white/25"
              @click="expand"
            >
              Voir le détail
            </UButton>
            <UButton
              size="xs"
              color="neutral"
              class="bg-white text-[var(--ui-error)] hover:bg-white/90"
              icon="i-lucide-log-out"
              @click="forceLogout"
            >
              Déconnexion
            </UButton>
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>
